import { memo, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { getButtonTestIdByText } from "e2e-tests/testIds";
import { useLocation, useMatch, useNavigate } from "react-router-dom";

import { ReactComponent as Arrow } from "@/assets/icons/arrow.svg";
import Logo from "@/common/Logo";
import Progress from "@/common/Progress";
import WebVersionLabel from "@/common/WebVersionLabel";
import PromoCodeOffer from "@/components/Promo/PromoCodeOffer";
import { logAnalyticsEvent, logPaltaAnalyticsEvent } from "@/constants/amplitude";
import { Colors } from "@/constants/colors";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { GAEvents, sendMetrics } from "@/constants/gtag";
import { Routes as AppRoutes } from "@/constants/routes";
import i18n from "@/localization/i18next";
import { Genders } from "@/types/gender";
import { useGender } from "@/utils/GenderProvider";

const NAVIGATION_TIMEOUT_MS = 500;

const genderLabels = {
    [Genders.Female]: i18n.t("female"),
    [Genders.Male]: i18n.t("male"),
};

const ChooseGender = () => {
    const [isLoaded, setLoaded] = useState(false);
    const { setGender } = useGender();
    const { search } = useLocation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isBothGoogleFlow = useMemo(
        () => pathname.includes(`/${AppRoutes.BothGoogle}`),
        [pathname]
    );

    const isOfferUrl = useMatch({
        path: `/${AppRoutes.Both}/${AppRoutes.Offer}`,
        end: false,
    });

    const loaderTitle = isBothGoogleFlow
        ? i18n.t("revolutionary_weight_loss_program")
        : i18n.t("zing_promotion");

    useEffect(() => {
        logPaltaAnalyticsEvent(WebEventTypes.VIEW_EVENT, {
            [WebEventProps.SCREEN]: "both_first_loader",
        });
    }, []);

    const handleLoaded = () => {
        logPaltaAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: "both_first_loader",
        });
        setLoaded(true);
    };

    const handleChoose = (gender: Genders) => () => {
        logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: "loader",
            [WebEventProps.RESULT]: gender,
        });
        sendMetrics(GAEvents.Gender, {
            [WebEventProps.RESULT]: gender,
        });
        setGender(gender);
        setTimeout(() => {
            navigate(
                {
                    pathname: gender,
                    search,
                },
                {
                    replace: true,
                    relative: "path",
                }
            );
        }, NAVIGATION_TIMEOUT_MS);
    };

    return (
        <div
            className={classNames(
                "zing-screen grid justify-center bg-loaderChoose bg-cover bg-center sm:bg-loaderChooseDesktop",
                { "bg-top": isOfferUrl }
            )}
        >
            {isOfferUrl && <PromoCodeOffer />}
            <div className="p-6">
                <Logo className="bg-no-repeat pb-6" fill={Colors.WHITE} />
            </div>
            <div className="svh-screen zing-max-w flex flex-col justify-end p-6 pb-[60px] md:pb-[125px] md:pt-20">
                <div className="grid gap-10">
                    <div className="grid gap-4">
                        <div className="text-zing-32-40 font-semibold text-white">
                            {loaderTitle}
                        </div>
                        <div className="text-zing-16-22 text-white">
                            {i18n.t("new_you_in_12_week")}
                        </div>
                    </div>
                    {isLoaded ? (
                        <div className="grid gap-2">
                            <a
                                onClick={handleChoose(Genders.Male)}
                                className="flex items-center justify-between rounded-2xl bg-zing-blue-200 p-4 text-white hover:cursor-pointer"
                                data-zing-test-id={getButtonTestIdByText(Genders.Male)}
                            >
                                <span className="text-zing-18-26 font-semibold capitalize">
                                    {genderLabels[Genders.Male]}
                                </span>
                                <span>
                                    <Arrow className="rotate-180" />
                                </span>
                            </a>
                            <a
                                onClick={handleChoose(Genders.Female)}
                                className="flex items-center justify-between rounded-2xl bg-zing-tangerine-400 p-4 text-white hover:cursor-pointer"
                                data-zing-test-id={getButtonTestIdByText(Genders.Female)}
                            >
                                <span className="text-zing-18-26 font-semibold capitalize">
                                    {genderLabels[Genders.Female]}
                                </span>
                                <span>
                                    <Arrow className="rotate-180" />
                                </span>
                            </a>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center gap-4">
                            <div className="flex flex-row">
                                <div className="after:content[''] text-zing-12-18 text-white after:ml-2 after:inline-block after:h-[10px] after:w-[10px] after:animate-spin after:rounded-[50%] after:border-2 after:border-l-[transparent]">
                                    {i18n.t("loading_quiz")}
                                </div>
                            </div>
                            <Progress onLoaded={handleLoaded} />
                        </div>
                    )}
                </div>
            </div>
            <WebVersionLabel />
        </div>
    );
};

export default memo(ChooseGender);
